import React, { useCallback, useEffect, useRef, useState } from "react";
import CampusSearch from "./general/CampusSearch";
import PrizeList from "./general/PrizeList";
import RaffleCountdownTimer from "./general/RaffleCountdownTimer";
import RaffleGoalProgress from "./general/RaffleGoalProgress";
import TeamsSection from "./teams/TeamsSection";
import TicketCheckout from "./ticketCheckout/TicketCheckout";
import Slider from "../common/Slider";
import { Helmet } from "react-helmet-async";
import { Fade } from "@material-ui/core";
import { ToastContainer, Slide, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  isMobileView,
  trimTrailingSlash,
  pluralizeText,
} from "../../lib/utils";
import {
  connectToSignalRMessages,
  subscribeToMessages,
} from "../../signalRConnection";
import SystemApi from "../../services/resources/SystemApi";

const purchaseTicketsButtonElementId = "purchase-tickets-button";
export const purchaseTicketsSectionElementId = "purchase-tickets";
export const checkoutSectionElementId = "checkout";

export default function RaffleByCampus(props) {
  const {
    hideCopy,
    history,
    history: {
      location: { pathname },
    },
    raffle: { endDate, gregorianYear, hebrewYear, raffleTitle },
    raffleEnrollment,
    raffleEnrollment: {
      allowPublicCreateReferrer,
      bonusRaffleGoal,
      campaignImages,
      campaignLogoUrl,
      campaignMessage,
      campusName,
      chabadHouseName,
      cociCcProcessingFeePercentage,
      currencyCode,
      defaultTicketOption,
      donorListDisplay,
      donorSiteURL,
      fullLegalText,
      isCociSeller,
      isDonorListPublic,
      // hasCashPrizeDisclaimer,
      payPalClientID,
      prizes,
      raffleGoal,
      raffleGoalMessage,
      referrers,
      stripePublicKey,
      ticketOptions,
      ticketsSold: _ticketsSold,
    },
    team,
    sellerId,
    sellerList,
    termsAndConditions,
    toggleShowTermsAndConditions,
    // trackPaymentClick,
    updateRaffleEnrollment,
  } = props;
  // consts
  const hasGoogleTagManagementTracking = !!(sellerId && isCociSeller);
  //refs
  const campusSearchRef = useRef(null);
  //state
  const [ticketsSold, setTicketsSold] = useState(_ticketsSold);
  const [teamTicketsSold, setTeamTicketsSold] = useState(team?.ticketsSold);
  const [newDonorsCount, setNewDonorsCount] = useState(0); //this will be set upon donors retrieval in DonorList
  const [donorsCount, setDonorsCount] = useState(0);
  const [systemLists, setSystemLists] = useState({
    associations: [],
    countries: [],
  });
  const [showStickyPurchaseButton, setShowStickyPurchaseButton] =
    useState(false);

  const onMessageReceived = useCallback(
    (data) => {
      const { TicketAmount, ReferrerID, DonorName, IsRefund } = data;

      if (!IsRefund && isDonorListPublic) {
        toast(
          `${DonorName} purchased ${TicketAmount} ${pluralizeText(
            "ticket",
            TicketAmount
          )}`
        );
        if (!team || ReferrerID === team.id) {
          //when on a team page, only update donor count if purchase is specifically for that team
          setDonorsCount((donorsCount) => donorsCount + 1);
          setNewDonorsCount((newDonorsCount) => newDonorsCount + 1);
        }
      }

      if (team && ReferrerID === team.id) {
        //when on a team page, update team ticket goalbar
        setTeamTicketsSold((teamTicketsSold) => teamTicketsSold + TicketAmount);
      }
      //always update the main ticket goalbar, even when on team page
      setTicketsSold((ticketsSold) => ticketsSold + TicketAmount);
    },
    [team, isDonorListPublic]
  );

  useEffect(() => {
    setTimeout(() => {
      window.scroll(0, 0);
    }, 100);
  }, []);

  useEffect(() => {
    // show the sticky Purchase Now button after scrolling past the static purchase button
    function handleScroll() {
      const purchaseButton = document.getElementById(
        purchaseTicketsButtonElementId
      );
      if (!purchaseButton) return;

      const purchaseButtonRect = purchaseButton.getBoundingClientRect();

      const checkoutSection = document.getElementById(checkoutSectionElementId);
      const checkoutSectionRect = checkoutSection?.getBoundingClientRect();
      const isCheckoutSectionVisible =
        checkoutSectionRect &&
        checkoutSectionRect.top < window.innerHeight &&
        checkoutSectionRect.bottom > 0;

      if (purchaseButtonRect.top < 0 && !isCheckoutSectionVisible) {
        setShowStickyPurchaseButton(true);
        // add margin to last element on page to sticky accommodate button height
        document.getElementById("root").lastElementChild.classList.add("mb-88");
      } else {
        setShowStickyPurchaseButton(false);
        document
          .getElementById("root")
          .lastElementChild.classList.remove("mb-88");
      }
    }

    if (isMobileView()) window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (sellerId) {
      const onConnectionSuccess = () => {
        subscribeToMessages(sellerId);
      };
      connectToSignalRMessages(onConnectionSuccess, onMessageReceived);
      //no need to unsubscribe because connection closes automatically on unmount/refresh
    }
  }, [sellerId, onMessageReceived]);

  useEffect(() => {
    if (hasGoogleTagManagementTracking) {
      const googleTagManagementBody = document.getElementById(
        "google-tag-management-body"
      );
      if (!googleTagManagementBody) {
        const script = document.createElement("iframe");
        script.src = "https://www.googletagmanager.com/ns.html?id=GTM-562DB7R";
        script.id = "google-tag-management-body";
        script.style.visibility = "hidden";
        script.style.display = "none";
        script.style.width = 0;
        script.style.height = 0;
        document.body.appendChild(script);
      }
    }
  }, [hasGoogleTagManagementTracking]);

  useEffect(() => {
    SystemApi.getSystemLists().then((sysLists) => {
      const { raffleDonorAssociations, countries } = sysLists;
      setSystemLists({
        associations: raffleDonorAssociations,
        countries,
      });
    });
  }, []);

  const navToPurchaseTickets = () => {
    const purchaseSection = document.getElementById(
      purchaseTicketsSectionElementId
    );
    if (purchaseSection) {
      purchaseSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        theme="light"
        hideProgressBar
        autoClose={3000}
        transition={Slide}
        closeOnClick={true}
      />
      <Helmet prioritizeSeoTags>
        <title>
          {sellerId
            ? `${chabadHouseName || "Chabad on Campus"} Grand Draw`
            : `Chabad on Campus Grand Draw ${gregorianYear}`}
        </title>
        <meta
          name="description"
          content={`Join our grand draw and partner with us in building the Jewish future${
            sellerId && chabadHouseName
              ? ` ${isCociSeller ? "with" : "at"} ${chabadHouseName}`
              : ""
          }.`}
        />
        <meta
          name="keywords"
          content={`pledgejewish,${chabadHouseName},${campusName},raffle,grand draw,prize draw`}
        />
        <meta
          property="og:image"
          content={
            (sellerId &&
              (campaignImages.find((i) => i.isPrimary) || campaignImages[0]) // first primary or first campaign image
                ?.url) ||
            campaignLogoUrl
          }
        />
        {hasGoogleTagManagementTracking &&
          !document.getElementById("google-tag-management-head") && (
            <script>
              {(function (w, d, s, l, i) {
                w[l] = w[l] || [];
                w[l].push({
                  "gtm.start": new Date().getTime(),
                  event: "gtm.js",
                });
                var f = d.getElementsByTagName(s)[0],
                  j = d.createElement(s),
                  dl = l !== "dataLayer" ? "&l=" + l : "";
                j.async = true;
                j.id = "google-tag-management-head";
                j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
                f.parentNode.insertBefore(j, f);
              })(window, document, "script", "dataLayer", "GTM-562DB7R")}
            </script>
          )}
      </Helmet>
      <RaffleCountdownTimer endDate={endDate} sellerId={sellerId} />
      <header className="header">
        <div className="background">
          {isMobileView() ? (
            <img
              src="/images/mobile-background.jpg"
              alt="background"
              style={{ height: "100%", width: "100%", objectFit: "cover" }}
            />
          ) : (
            <>
              <video
                autoPlay="autoplay"
                muted={true}
                loop
                id="myVideo"
                preload="true"
              >
                <source src="/images/bg-flow.mp4" type="video/mp4" />
                Your browser does not support HTML5 video.
              </video>
              <div className="video-overlay" />
              <div className="background-text">
                Keep them
                <br />
                Growing.
                <br />
                proud.
                <br />
                wrapping.
                <br />
                Lighting.
                <br />
                giving.
                <br />
                Learning.
              </div>
            </>
          )}
        </div>
        <div className="header-title">
          <p className="fs-20 fw-900 text-center uppercase-text">
            {sellerId ? chabadHouseName : "Chabad on Campus"}
          </p>
          <p className="bh">ב״ה</p>
        </div>
        {!!team && (
          <div className="flex flex-justify-center">
            <p className="team-name-banner">{team.name} Team Page</p>
          </div>
        )}
        <div className="header-logo">
          <img src={campaignLogoUrl} alt="" />
          <p
            className="fs-50 fw-900 light-text mt-8 mb-40"
            style={{ textShadow: "0 0 4px rgba(0, 0, 0, 0.25)" }}
          >
            {`${gregorianYear}/${hebrewYear}`}
          </p>
        </div>
        <div className="campaign-message">
          <p
            className="fs-30 fw-800 light-text text-center mb-32"
            style={{
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
              lineHeight: 1.3,
              whiteSpace: "pre-wrap",
            }}
          >
            {campaignMessage}
          </p>
        </div>
        {!!team && (
          <div className="raffle-back-to-home">
            <a href={donorSiteURL}>
              <span className="flex flex-align-center font-medium no-wrap">
                <i className="material-icons back-arrow mr-8">arrow_back</i>
                Main Campaign
              </span>
            </a>
          </div>
        )}
        <div className="flex flex-justify-center">
          <button
            className="btn btn-rounded mb-40"
            id={sellerId ? purchaseTicketsButtonElementId : ""}
            onClick={() =>
              sellerId
                ? navToPurchaseTickets()
                : campusSearchRef.current.scrollIntoView({ behavior: "smooth" })
            }
          >
            {sellerId ? "Buy tickets now" : "Select a campus"}
            <i
              className="material-icons"
              style={{ fontSize: 20, marginLeft: 8 }}
            >
              navigate_next
            </i>
          </button>
        </div>
        {!isMobileView() && (
          <PrizeList
            // hasCashPrizeDisclaimer={hasCashPrizeDisclaimer}
            onPrizeClick={navToPurchaseTickets}
            prizes={prizes}
            raffleTitle={raffleTitle}
          />
        )}
      </header>
      {isMobileView() && (
        <>
          <Fade in={showStickyPurchaseButton}>
            <div className="sticky-purchase-button">
              <button
                className="btn btn-rounded"
                onClick={navToPurchaseTickets}
              >
                Buy tickets now
                <i
                  className="material-icons"
                  style={{ fontSize: 20, marginLeft: 8 }}
                >
                  navigate_next
                </i>
              </button>
            </div>
          </Fade>
          <div style={{ background: "#1EA17E", padding: "32px 24px 32px" }}>
            <PrizeList
              onPrizeClick={navToPurchaseTickets}
              prizes={prizes}
              raffleTitle={raffleTitle}
            />
          </div>
        </>
      )}
      {!sellerId && (
        <div ref={campusSearchRef}>
          <CampusSearch
            goToRaffleByCampus={(sellerSlug) => {
              history.replace(`/${sellerSlug}`);
              window.location.reload();
            }}
            sellerList={sellerList}
          />
        </div>
      )}
      {!!campaignImages?.length && <Slider campaignImages={campaignImages} />}
      {sellerId && raffleGoal && (
        <RaffleGoalProgress
          bonusRaffleGoal={bonusRaffleGoal}
          message={raffleGoalMessage}
          raffleGoal={raffleGoal}
          raffleTitle={raffleTitle}
          team={team}
          teamGoal={team?.goal}
          teamTicketsSold={teamTicketsSold}
          ticketsSold={ticketsSold}
        />
      )}
      {sellerId && (
        <TicketCheckout
          chabadHouseName={chabadHouseName}
          cociCcProcessingFeePercentage={cociCcProcessingFeePercentage}
          currencyCode={currencyCode}
          defaultTicketOption={defaultTicketOption}
          donorListDisplay={donorListDisplay}
          gregorianYear={gregorianYear}
          // hasCashPrizeDisclaimer={hasCashPrizeDisclaimer}
          isDonorListPublic={!!sellerId && isDonorListPublic}
          legalText={fullLegalText}
          onCompletePurchase={(donorEmail) => {
            window.scrollTo({ behavior: "instant", left: 0, top: 0 }); // reset scroll before navigating
            const redirectPath = `${trimTrailingSlash(
              pathname.replace(team?.slug, "")
            )}/purchase-complete${hideCopy ? "?copy=false" : ""}`;
            history.replace(redirectPath, { donorEmail });
          }}
          payPalClientID={payPalClientID}
          raffleEnrollment={raffleEnrollment}
          raffleTitle={raffleTitle}
          referrers={!!sellerId ? referrers : []}
          sellerId={sellerId}
          stripePublicKey={stripePublicKey}
          systemLists={systemLists}
          termsAndConditions={termsAndConditions}
          toggleShowTermsAndConditions={toggleShowTermsAndConditions}
          team={team}
          ticketOptions={ticketOptions}
          // trackPaymentClick={trackPaymentClick}
        />
      )}
      {(isDonorListPublic ||
        team ||
        (!!sellerId &&
          !team &&
          (allowPublicCreateReferrer || referrers?.length))) && (
        <TeamsSection
          currencyCode={currencyCode}
          donorListDisplay={donorListDisplay}
          donorsCount={donorsCount}
          donorSiteURL={donorSiteURL}
          isDonorListPublic={isDonorListPublic}
          newDonorsCount={newDonorsCount}
          onCreateTeam={(team) =>
            updateRaffleEnrollment((e) => ({
              ...e,
              referrers: [...e.referrers, team],
            }))
          }
          raffleEnrollment={raffleEnrollment}
          setDonorsCount={setDonorsCount}
          setNewDonorsCount={setNewDonorsCount}
          systemLists={systemLists}
          team={team}
          teamPageURL={(teamSlug) => donorSiteURL + "/" + teamSlug}
          teams={referrers}
        />
      )}
    </>
  );
}
